import {createEntityAdapter, createSlice, EntityId, PayloadAction} from '@reduxjs/toolkit';
import {mapValues} from 'lodash';
import {
  EmergencyContraceptionKey,
  QuestionnaireEntity,
  MedicalQuestionnaireKey,
  MethodKey,
  PackItem,
  MedicationItem,
  AnswerData,
  Products,
} from '~/redux/questionnaires/types';
import {RootState} from '~/redux/root-reducer';
import {ReducerState} from '~/utils/thunk-api/type';
import {
  addQuestionnaire,
  deleteQuestionnaire,
  fetchQuestionnaires,
  fetchQuestionnairesAnswerById,
  updateQuestionnaire,
} from '~/redux/questionnaires/thunk';
import {
  addIdsToSources,
  createSelectAllBySource,
  deleteSuccess,
  fetchSuccess,
  upsertMany,
  upsertOneMutably,
} from '~/utils/thunk-api';
import {DEFAULT_SOURCE_REDUCER_STATE} from '~/utils/thunk-api/constants';
import {SOURCES_QUESTIONNAIRE} from '~/redux/questionnaires/entity-config';
import {getObjNthItem} from '~/utils/helpers';

// Fixme Add this slice to src/redux/root-reducer.ts then remove this block
// Fixme Add this slice to src/redux/root-reducer.ts then remove this block
// Fixme Add this slice to src/redux/root-reducer.ts then remove this block
// Fixme Add this slice to src/redux/root-reducer.ts then remove this block
// Fixme Add this slice to src/redux/root-reducer.ts then remove this block

interface InitialState extends ReducerState {
  activeQuestion: {index: number; class: string; keys: string[]};
  medicalQuestionnaire: QuestionnaireEntity;
  selectedMedication: MedicationItem | Products;
  methodKey: string;
  selectedBrand: string;
  isWebview: boolean;
  appointmentType: number | undefined;
  userQuestionnaireId: number | undefined;
  collectData: Record<MedicalQuestionnaireKey, AnswerData>;
  nextSlideKey: string;
  trackUserAuth: any;
  rebookingAppointment: {
    orderId: number | undefined;
    userQuestionId: number | undefined;
    typeAppt: number | undefined;
    key: string | undefined;
    time: number | undefined;
  };
}

const sources = mapValues(SOURCES_QUESTIONNAIRE, () => DEFAULT_SOURCE_REDUCER_STATE);

const questionnairesAdapter = createEntityAdapter<QuestionnaireEntity>();

const initialState = questionnairesAdapter.getInitialState<InitialState>({
  sources,
  activeQuestion: {
    index: 0,
    class: '',
    keys: ['START'],
  },
  medicalQuestionnaire: {} as QuestionnaireEntity,
  methodKey: '',
  selectedMedication: {} as MedicationItem,
  selectedBrand: '',
  isWebview: false,
  appointmentType: undefined,
  userQuestionnaireId: undefined,
  collectData: {} as Record<MedicalQuestionnaireKey, AnswerData>,
  nextSlideKey: '',
  trackUserAuth: {},
  rebookingAppointment: {
    orderId: undefined,
    userQuestionId: undefined,
    typeAppt: undefined,
    key: undefined,
    time: undefined,
  },
});

const questionnaires = createSlice({
  name: 'questionnaires',
  initialState,
  reducers: {
    setActiveQuestionnaire(state, {payload}: PayloadAction<{value: any}>) {
      const {value} = payload;
      state.activeQuestion = value;
    },
    setNextQuestionnaire(state, {payload}: PayloadAction<{value: MedicalQuestionnaireKey}>) {
      const {value} = payload;
      state.activeQuestion.keys.push(value);
    },
    setBackQuestionnaire(state, {payload}: PayloadAction<{key?: MedicalQuestionnaireKey}>) {
      const {key} = payload;
      if (key) {
        const index = state.activeQuestion.keys.findIndex(val => val === key);
        state.activeQuestion.keys = state.activeQuestion.keys.slice(0, index + 1);
      } else {
        state.activeQuestion.keys.pop();
      }
    },
    setQuestionnaireAnswer(
      state,
      {
        payload,
      }: PayloadAction<{key: MedicalQuestionnaireKey; index: string; data: any; questions?: Array<Record<any, any>>}>,
    ) {
      const {key, data, index, questions} = payload;
      if (!state.collectData[key]) {
        state.collectData[key] = {} as AnswerData;
      }
      if (!!questions && questions.length > 0) {
        state.collectData[key].questions = questions;
      }
      state.collectData[key].data = data;
      state.collectData[key].index = index;
    },
    setMethodKey(state, {payload}: PayloadAction<{key: string}>) {
      const {key} = payload;
      state.methodKey = key;
    },
    setSelectedMedication(state, {payload}: PayloadAction<{data: MedicationItem | Products}>) {
      const {data} = payload;
      state.selectedMedication = data;
    },
    setSelectedBrand(state, {payload}: PayloadAction<{data?: string}>) {
      const {data} = payload;
      if (data) state.selectedBrand = data;
    },
    setIsWebview(state, {payload}: PayloadAction<{data: boolean}>) {
      const {data} = payload;
      state.isWebview = data;
    },
    setAppointmentType(state, {payload}: PayloadAction<{type?: number}>) {
      const {type} = payload;
      state.appointmentType = type;
    },
    setUserQuestionnaireId(state, {payload}: PayloadAction<{id?: number}>) {
      const {id} = payload;
      state.userQuestionnaireId = id;
    },
    setPopupNextSlideKey(state, {payload}) {
      state.nextSlideKey = payload;
    },
    setTrackUserAuth(state, {payload}) {
      state.trackUserAuth = payload;
    },
    setRebookingAppointment(state, {payload}) {
      state.rebookingAppointment = payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchQuestionnaires.fulfilled, (state, {payload}) => {
      state.medicalQuestionnaire = payload;
    });
    builder.addCase(addQuestionnaire.fulfilled, (state, {payload}) => {
      // state.activeQuestion = {index: 0, class: '', keys: ['START']};
      // state.selectedMedication = {} as MedicationItem;
      // state.selectedBrand = '';
    });
    builder.addCase(updateQuestionnaire.fulfilled, (state, {payload}) => {
      upsertOneMutably(questionnairesAdapter, state, payload.normalized.entities.questionnaires);
    });
    builder.addCase(deleteQuestionnaire.fulfilled, (state, {payload, meta}) => {
      const {arg} = meta;
      questionnairesAdapter.removeOne(state, arg?.id as EntityId);
      deleteSuccess(state, arg?.id);
    });
    builder.addCase(fetchQuestionnairesAnswerById.fulfilled, (state, {payload}: PayloadAction<any>) => {
      const {data} = payload;
      if (data) {
        state.collectData = data;
      }
    });
  },
});

export const {
  setActiveQuestionnaire,
  setNextQuestionnaire,
  setBackQuestionnaire,
  setQuestionnaireAnswer,
  setMethodKey,
  setSelectedMedication,
  setSelectedBrand,
  setIsWebview,
  setAppointmentType,
  setUserQuestionnaireId,
  setPopupNextSlideKey,
  setTrackUserAuth,
  setRebookingAppointment,
} = questionnaires.actions;

export default questionnaires.reducer;

export const {
  selectById: selectQuestionnaireById,
  selectIds: selectQuestionnairesIds,
  selectEntities: selectQuestionnairesEntities,
  selectAll: selectAllQuestionnaires,
  selectTotal: selectTotalQuestionnaires,
} = questionnairesAdapter.getSelectors((state: RootState) => state.questionnaires);

export const [getQuestionnaires] = createSelectAllBySource('questionnaires', selectQuestionnairesEntities, [
  SOURCES_QUESTIONNAIRE.QUESTIONNAIRES_PAGE,
]);
